import { Icons } from '@/components/icons';
import { Button } from '@/components/primitives/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/primitives/Dialog';
import { Typography } from '@/components/primitives/Typography';
import { tenantSelector } from '@/features/configuration/configurationSelector';
import { localeConfigurations, useTranslation } from '@/i18n';
import { TransText } from '@/i18n/trans/text';
import { useSelector } from '@/store/utils';
import { typedEntries } from '@/utils/object';
import { getTenantConfiguration } from '@/utils/tenant';
import { useTranslation as useWidgetTranslation } from '@turnit-ride-ui/webshop-search-widget';
import type { FC } from 'react';
import { useState } from 'react';

interface MobileLanguageSelectProps {}

const MobileLanguageSelect: FC<MobileLanguageSelectProps> = () => {
  const [open, setOpen] = useState(false);
  const {
    i18n: { changeLanguage, language: currentLanguage },
  } = useTranslation();
  const {
    i18n: { changeLanguage: changeWidgetLanguage },
  } = useWidgetTranslation();
  const currentTenant = useSelector(tenantSelector);
  const tenantConfiguration = getTenantConfiguration(currentTenant.value);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger className="flex items-center px-0" asChild>
        <Button variant="ghostLink" data-testid="language-select-trigger">
          <Icons.globe width={24} height={24} />
          <Typography variant="body1">
            {currentLanguage.toUpperCase()}
          </Typography>
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader className="p-4 text-left">
          <DialogTitle>
            <TransText i18nKey="selectLanguage" />
          </DialogTitle>
          <DialogDescription hidden>
            <TransText i18nKey="selectLanguage" />
          </DialogDescription>
        </DialogHeader>
        <div className="[&>*:last-child]:rounded-md">
          {typedEntries(localeConfigurations)
            .filter(([code]) =>
              tenantConfiguration?.languageOptions.includes(code)
            )
            .map(([code, config], key) => (
              <Button
                key={key}
                variant="ghostLink"
                onClick={() => {
                  changeLanguage(code);
                  changeWidgetLanguage(code);
                  setOpen(false);
                }}
                data-testid={`language-select-choose-${code}`}
                className="h-14 justify-between rounded-none p-4 hover:bg-primary-light"
                fullWidth
              >
                <Typography variant="paragraph">{config.label}</Typography>
                <div className="flex items-center gap-2">
                  {code === currentLanguage && (
                    <Icons.checkCircle
                      height={20}
                      width={20}
                      className="text-success-text"
                    />
                  )}
                  <Typography variant="paragraph">
                    {code.toUpperCase()}
                  </Typography>
                </div>
              </Button>
            ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MobileLanguageSelect;
